/**
 * 我的设备 —— 控制台首页：设备卡片墙 / 筛选排序 / 控制 / 历史曲线 / 添加设备向导。
 * 实时同步：useStream 把服务端 SSE 事件合并进 react-query 缓存（多窗口一致）。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Device } from '@znjj/shared';
import { deviceKeys, devicesApi, type DeviceList } from '../api/devices';
import { AddDeviceSheet } from '../components/AddDeviceSheet';
import { DeviceCard } from '../components/DeviceCard';
import { HistorySheet } from '../components/HistorySheet';
import { Icon } from '../components/Icon';
import { EmptyState, Sheet, toast } from '../components/ui';
import { useStream } from '../hooks/useStream';

type FilterKey = 'all' | 'online' | 'offline' | 'unclaimed';
const SORTS = ['默认排序', '按房间排序', '按在线状态排序'] as const;
const ROOM_ORDER = ['客厅', '厨房', '工作室', '卧室', '院子', '全屋'];

export function ConsolePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  useStream(true);

  const listQuery = useQuery({ queryKey: deviceKeys.list, queryFn: devicesApi.list });
  const devices = listQuery.data?.list ?? [];

  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortMode, setSortMode] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  /* 指令下发：先乐观更新本地缓存，真实状态由 SSE 收敛 */
  const command = useMutation({
    mutationFn: (p: { id: string; code: string; value: unknown }) =>
      devicesApi.command(p.id, p.code, p.value),
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: deviceKeys.list });
      const prev = qc.getQueryData<DeviceList>(deviceKeys.list);
      qc.setQueryData<DeviceList>(deviceKeys.list, (old) =>
        old
          ? {
              ...old,
              list: old.list.map((d) =>
                d.id === p.id ? { ...d, state: { ...d.state, [p.code]: p.value } } : d,
              ),
            }
          : old,
      );
      return { prev };
    },
    onError: (err: Error, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(deviceKeys.list, ctx.prev);
      toast.err(err.message);
    },
  });

  const claim = useMutation({
    mutationFn: (p: { id: string; claimed: boolean }) =>
      p.claimed ? devicesApi.unclaim(p.id) : devicesApi.claim(p.id),
    onSuccess: (res, p) => {
      void qc.invalidateQueries({ queryKey: deviceKeys.list });
      toast.ok(
        p.claimed
          ? `「${res.device.name}」已取消认领，语音助手将忽略该设备`
          : `「${res.device.name}」已认领，语音助手现在可以控制它`,
      );
    },
    onError: (err: Error) => toast.err(err.message),
  });

  /* 删除设备：连同状态与历史数据一起清除，二次确认后执行 */
  const remove = useMutation({
    mutationFn: (id: string) => devicesApi.remove(id),
    onSuccess: (_res, id) => {
      void qc.invalidateQueries({ queryKey: deviceKeys.list });
      toast.ok(`「${devices.find((d) => d.id === id)?.name ?? '设备'}」已删除`);
      setDeleteId(null);
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const counts = useMemo(
    () => ({
      all: devices.length,
      online: devices.filter((d) => d.online).length,
      offline: devices.filter((d) => !d.online).length,
      unclaimed: devices.filter((d) => !d.claimedAt).length,
    }),
    [devices],
  );

  const visible = useMemo(() => {
    let list = devices.slice();
    if (filter === 'online') list = list.filter((d) => d.online);
    if (filter === 'offline') list = list.filter((d) => !d.online);
    if (filter === 'unclaimed') list = list.filter((d) => !d.claimedAt);
    if (sortMode === 1) list.sort((a, b) => ROOM_ORDER.indexOf(a.room ?? '') - ROOM_ORDER.indexOf(b.room ?? ''));
    if (sortMode === 2) list.sort((a, b) => Number(b.online) - Number(a.online));
    return list;
  }, [devices, filter, sortMode]);

  const historyDevice: Device | undefined = devices.find((d) => d.id === historyId);
  const deleting: Device | undefined = devices.find((d) => d.id === deleteId);

  const filters: { key: FilterKey; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'online', label: '在线' },
    { key: 'offline', label: '离线' },
    { key: 'unclaimed', label: '未认领' },
  ];

  return (
    <>
      <div className="page-head">
        <div className="row row--between">
          <div>
            <h1 className="page-title">我的设备</h1>
            <p className="page-sub">
              {listQuery.isPending
                ? '正在载入设备…'
                : `${counts.online}/${counts.all} 在线 · ${counts.offline} 台离线 · ${counts.unclaimed} 台未认领`}
            </p>
          </div>
          <button className="btn btn--sm btn--primary" type="button" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={15} />
            添加设备
          </button>
        </div>
      </div>

      <div className="row row--between row--wrap" style={{ marginBottom: 12, alignItems: 'center' }}>
        <div className="row row--wrap">
          {filters.map((f) => (
            <button
              key={f.key}
              className={'chip' + (filter === f.key ? ' is-on' : '')}
              type="button"
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="num">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <button
          className="btn btn--sm btn--ghost"
          type="button"
          onClick={() => setSortMode((m) => (m + 1) % SORTS.length)}
        >
          <Icon name="list-filter" size={14} />
          {SORTS[sortMode]}
        </button>
      </div>

      {listQuery.isPending ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="skeleton skeleton--card" />
          <div className="skeleton skeleton--card" />
        </div>
      ) : visible.length ? (
        <div className="stack" style={{ gap: 12 }}>
          {visible.map((d, i) => (
            <DeviceCard
              key={d.id}
              device={d}
              index={i}
              onCommand={(code, value) => command.mutate({ id: d.id, code, value })}
              onHistory={() => setHistoryId(d.id)}
              onToggleClaim={() => claim.mutate({ id: d.id, claimed: !!d.claimedAt })}
              onShare={() => navigate(`/community?share=${d.id}`)}
              onDelete={() => setDeleteId(d.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon="box"
          title="没有符合条件的设备"
          desc="换一个筛选条件，或者添加一台新的智能设备。"
          action={
            devices.length ? (
              <button className="btn btn--sm" type="button" onClick={() => setFilter('all')}>
                显示全部设备
              </button>
            ) : (
              <button className="btn btn--sm btn--primary" type="button" onClick={() => setAddOpen(true)}>
                添加第一台设备
              </button>
            )
          }
        />
      )}

      <div className="card card--flat" style={{ margin: '16px 0 24px' }}>
        <p className="card__desc">
          设备来源覆盖 <strong>模拟设备 / 绑定码接入的真机 / 涂鸦云 / 米家</strong>。
          真机固件内置产品密钥，上电后自动到平台自注册并显示 6 位绑定码，
          在「添加设备 → 绑定码绑定」里输码即归属当前账号，之后由控制台与语音助手共同控制。
        </p>
      </div>

      <AddDeviceSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(d) => toast.ok(`「${d.name}」已添加`)}
      />

      {historyDevice ? (
        <HistorySheet
          key={historyDevice.id}
          device={historyDevice}
          open={!!historyId}
          onClose={() => setHistoryId(null)}
        />
      ) : null}

      {deleting ? (
        <Sheet open onClose={() => setDeleteId(null)} title="删除设备">
          <p className="card__desc">
            将删除「{deleting.name}」（{deleting.spec.label} · 编号 <span className="num">{deleting.id.slice(0, 10)}</span>），
            其当前状态与历史遥测数据一并清除，且不可恢复。设备若仍在通电上报，可在「添加设备」里重新接入。
          </p>
          <div className="row" style={{ gap: 10, marginTop: 18 }}>
            <button className="btn btn--block" type="button" onClick={() => setDeleteId(null)}>
              取消
            </button>
            <button
              className="btn btn--block btn--danger"
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate(deleting.id)}
            >
              {remove.isPending ? '删除中…' : '确认删除'}
            </button>
          </div>
        </Sheet>
      ) : null}
    </>
  );
}
